import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Eye, EyeOff, Sparkles, ArrowRight, AlertCircle, CheckCircle2, ShieldAlert, Building2 } from 'lucide-react';
import { auth, type ApiError, type InviteInfo } from '../services/api';

const Register: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('token');

  const [inviteLoading, setInviteLoading] = useState(!!inviteToken);
  const [inviteError, setInviteError] = useState('');
  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [consentAccepted, setConsentAccepted] = useState(false);

  useEffect(() => {
    if (!inviteToken) return;
    auth.validateInvite(inviteToken)
      .then((info) => {
        setInviteInfo(info);
        setEmail(info.email);
        setFullName(info.contactName ?? '');
      })
      .catch((err) => {
        const apiErr = err as ApiError;
        setInviteError(apiErr.message || 'This invite link is invalid or has expired.');
      })
      .finally(() => setInviteLoading(false));
  }, [inviteToken]);

  const passwordStrength = (() => {
    if (password.length === 0) return 0;
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    return score;
  })();

  const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong'][passwordStrength];
  const strengthColor = ['', '#EF4444', '#F59E0B', '#10B981', '#1A6CF6'][passwordStrength];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (!consentAccepted) {
      setError('Please accept the Privacy Policy and Terms of Service to continue.');
      return;
    }

    setIsLoading(true);
    try {
      await auth.register({ fullName, email, password, inviteToken: inviteToken ?? undefined });
      navigate('/login', { replace: true, state: { registered: true, email } });
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr.status === 409) {
        setError('This email address is already registered. Please sign in instead.');
      } else {
        setError(apiErr.message || 'Registration failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '11px 14px',
    background: 'var(--nx-surface-2)',
    border: '1px solid var(--nx-border)',
    borderRadius: 10,
    fontSize: 14,
    color: 'var(--nx-text-primary)',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 150ms',
  };

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) =>
    (e.currentTarget.style.borderColor = '#1A6CF6');
  const onBlur = (e: React.FocusEvent<HTMLInputElement>) =>
    (e.currentTarget.style.borderColor = 'var(--nx-border)');

  const pageShell = (children: React.ReactNode) => (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--nx-navy)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        fontFamily: 'var(--font-body)',
      }}
    >
      {children}
    </div>
  );

  // Block access if no invite token in the URL
  if (!inviteToken) {
    return pageShell(
      <div
        style={{
          maxWidth: 400,
          width: '100%',
          background: 'var(--nx-surface)',
          borderRadius: 20,
          border: '1px solid rgba(239,68,68,0.2)',
          overflow: 'hidden',
          textAlign: 'center',
        }}
      >
        <div style={{ height: 3, background: 'linear-gradient(90deg, #EF4444, #DC2626)' }} />
        <div style={{ padding: '40px 32px' }}>
          <ShieldAlert size={40} style={{ color: '#EF4444', marginBottom: 16 }} />
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: 20, fontWeight: 800, color: 'var(--nx-text-primary)', marginBottom: 8 }}>
            Invite link required
          </h2>
          <p style={{ fontSize: 13, color: 'var(--nx-text-muted)', lineHeight: 1.6, marginBottom: 24 }}>
            Registration is by invite only. Please use the link sent to your email, or{' '}
            <Link to="/request-access" style={{ color: 'var(--nx-blue)', textDecoration: 'none' }}>request access</Link>.
          </p>
          <Link to="/login" style={{ fontSize: 13, color: 'var(--nx-text-secondary)', textDecoration: 'none' }}>
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  // Validating invite token
  if (inviteLoading) {
    return pageShell(
      <div
        style={{
          maxWidth: 400,
          width: '100%',
          background: 'var(--nx-surface)',
          borderRadius: 20,
          border: '1px solid rgba(255,255,255,0.08)',
          overflow: 'hidden',
          textAlign: 'center',
        }}
      >
        <div style={{ height: 3, background: 'linear-gradient(90deg, #6D28D9, #1A6CF6)' }} />
        <div style={{ padding: '48px 32px' }}>
          <div
            style={{
              width: 32,
              height: 32,
              border: '3px solid rgba(26,108,246,0.2)',
              borderTopColor: '#1A6CF6',
              borderRadius: '50%',
              animation: 'nx-spin 0.7s linear infinite',
              margin: '0 auto 16px',
            }}
          />
          <p style={{ fontSize: 14, color: 'var(--nx-text-secondary)' }}>Verifying your invite...</p>
        </div>
      </div>
    );
  }

  // Invite is invalid / expired / revoked
  if (inviteError) {
    return pageShell(
      <div
        style={{
          maxWidth: 400,
          width: '100%',
          background: 'var(--nx-surface)',
          borderRadius: 20,
          border: '1px solid rgba(239,68,68,0.2)',
          overflow: 'hidden',
          textAlign: 'center',
        }}
      >
        <div style={{ height: 3, background: 'linear-gradient(90deg, #EF4444, #DC2626)' }} />
        <div style={{ padding: '40px 32px' }}>
          <ShieldAlert size={40} style={{ color: '#EF4444', marginBottom: 16 }} />
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: 20, fontWeight: 800, color: 'var(--nx-text-primary)', marginBottom: 8 }}>
            Invite link invalid
          </h2>
          <p style={{ fontSize: 13, color: 'var(--nx-text-muted)', lineHeight: 1.6, marginBottom: 24 }}>
            {inviteError}
          </p>
          <Link to="/request-access" style={{ fontSize: 13, color: 'var(--nx-blue)', textDecoration: 'none', fontWeight: 500 }}>
            Request a new invite
          </Link>
          <span style={{ fontSize: 13, color: 'var(--nx-text-muted)', margin: '0 8px' }}>·</span>
          <Link to="/login" style={{ fontSize: 13, color: 'var(--nx-text-secondary)', textDecoration: 'none' }}>
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--nx-navy)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: 'var(--font-body)',
      }}
    >
      {/* Grid background */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(26,108,246,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(26,108,246,0.05) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
          pointerEvents: 'none',
        }}
      />

      {/* Radial glow */}
      <div
        style={{
          position: 'absolute',
          width: '600px',
          height: '500px',
          background:
            'radial-gradient(ellipse at 50% 40%, rgba(109,40,217,0.14) 0%, rgba(26,108,246,0.07) 40%, transparent 70%)',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
        }}
      />

      {/* Card */}
      <div
        className="nx-anim-fade-up"
        style={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: 440,
          background: 'var(--nx-surface)',
          borderRadius: 20,
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 40px 120px rgba(0,0,0,0.5), 0 0 0 1px rgba(109,40,217,0.08)',
          overflow: 'hidden',
        }}
      >
        {/* Top gradient bar */}
        <div style={{ height: 3, background: 'linear-gradient(90deg, #6D28D9, #1A6CF6)' }} />

        <div style={{ padding: '36px 32px' }}>
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 32 }}>
            <div
              style={{
                width: 36,
                height: 36,
                background: 'linear-gradient(135deg, #1A6CF6, #6D28D9)',
                borderRadius: 9,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 14, color: '#fff' }}>
                Nx
              </span>
            </div>
            <div>
              <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 16, color: 'var(--nx-text-primary)', letterSpacing: '-0.02em' }}>
                NEXORA
              </div>
              <div style={{ fontSize: 10, color: 'var(--nx-text-muted)', letterSpacing: '0.08em' }}>
                Pentagon Analytics
              </div>
            </div>
          </div>

          {/* Header */}
          <div style={{ marginBottom: 28 }}>
            <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 24, fontWeight: 800, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 6 }}>
              Create account
            </h1>
            <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>
              Set up your Nexora admin account
            </p>
          </div>

          {/* Invite info banner */}
          {inviteInfo && (
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                padding: '12px 14px',
                background: 'linear-gradient(135deg, rgba(26,108,246,0.08), rgba(109,40,217,0.05))',
                border: '1px solid rgba(26,108,246,0.18)',
                borderRadius: 10,
                marginBottom: 20,
              }}
            >
              <Building2 size={15} style={{ color: '#1A6CF6', flexShrink: 0, marginTop: 1 }} />
              <div>
                <p style={{ fontSize: 13, color: 'var(--nx-text-primary)', fontWeight: 600, marginBottom: 2 }}>
                  {inviteInfo.societyName}
                </p>
                <p style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>
                  Plan: <span style={{ color: 'var(--nx-text-secondary)', fontWeight: 500 }}>
                    {inviteInfo.plan.charAt(0).toUpperCase() + inviteInfo.plan.slice(1).toLowerCase()}
                  </span>
                </p>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '12px 14px',
                background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.2)',
                borderRadius: 10,
                marginBottom: 20,
              }}
            >
              <AlertCircle size={15} style={{ color: '#EF4444', flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: '#EF4444' }}>{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Full name */}
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 6, letterSpacing: '0.04em' }}>
                FULL NAME
              </label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Suresh Iyer"
                required
                autoFocus
                style={inputStyle}
                onFocus={onFocus}
                onBlur={onBlur}
              />
            </div>

            {/* Email */}
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 6, letterSpacing: '0.04em' }}>
                EMAIL ADDRESS
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => !inviteInfo && setEmail(e.target.value)}
                placeholder="admin@nexora.io"
                required
                readOnly={!!inviteInfo}
                style={{
                  ...inputStyle,
                  ...(inviteInfo ? { background: 'var(--nx-surface-3)', color: 'var(--nx-text-secondary)', cursor: 'default' } : {}),
                }}
                onFocus={inviteInfo ? undefined : onFocus}
                onBlur={inviteInfo ? undefined : onBlur}
              />
            </div>

            {/* Password */}
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 6, letterSpacing: '0.04em' }}>
                PASSWORD
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 8 characters"
                  required
                  style={{ ...inputStyle, paddingRight: 42 }}
                  onFocus={onFocus}
                  onBlur={onBlur}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-text-muted)', display: 'flex', alignItems: 'center', padding: 0 }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>

              {/* Strength bar */}
              {password.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                    {[1, 2, 3, 4].map((level) => (
                      <div
                        key={level}
                        style={{
                          flex: 1,
                          height: 3,
                          borderRadius: 2,
                          background: passwordStrength >= level ? strengthColor : 'var(--nx-surface-3)',
                          transition: 'background 200ms',
                        }}
                      />
                    ))}
                  </div>
                  <span style={{ fontSize: 11, color: strengthColor }}>{strengthLabel}</span>
                </div>
              )}
            </div>

            {/* Confirm password */}
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 6, letterSpacing: '0.04em' }}>
                CONFIRM PASSWORD
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  required
                  style={{
                    ...inputStyle,
                    paddingRight: 42,
                    borderColor:
                      confirmPassword.length > 0
                        ? confirmPassword === password
                          ? '#10B981'
                          : '#EF4444'
                        : 'var(--nx-border)',
                  }}
                  onFocus={onFocus}
                  onBlur={onBlur}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-text-muted)', display: 'flex', alignItems: 'center', padding: 0 }}
                >
                  {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
                {confirmPassword.length > 0 && confirmPassword === password && (
                  <CheckCircle2
                    size={15}
                    style={{ position: 'absolute', right: 38, top: '50%', transform: 'translateY(-50%)', color: '#10B981' }}
                  />
                )}
              </div>
            </div>

            {/* Consent */}
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: 'var(--nx-text-secondary)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={consentAccepted}
                onChange={(e) => setConsentAccepted(e.target.checked)}
                required
                style={{ marginTop: 2, cursor: 'pointer' }}
              />
              <span>
                I agree to Nexora's{' '}
                <Link to="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--nx-blue)', textDecoration: 'none', fontWeight: 500 }}>
                  Privacy Policy
                </Link>{' '}
                and{' '}
                <Link to="/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--nx-blue)', textDecoration: 'none', fontWeight: 500 }}>
                  Terms of Service
                </Link>, including how my personal data is collected and processed.
              </span>
            </label>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading || !consentAccepted}
              className="nx-btn nx-btn-primary"
              style={{
                width: '100%',
                justifyContent: 'center',
                padding: '12px',
                fontSize: 14,
                fontWeight: 600,
                marginTop: 4,
                opacity: isLoading ? 0.7 : 1,
                cursor: isLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {isLoading ? (
                <>
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTopColor: '#fff',
                      borderRadius: '50%',
                      animation: 'nx-spin 0.7s linear infinite',
                      display: 'inline-block',
                    }}
                  />
                  Creating account...
                </>
              ) : (
                <>
                  Create Account <ArrowRight size={15} />
                </>
              )}
            </button>
          </form>

          {/* Footer info */}
          <div
            style={{
              marginTop: 20,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '12px 14px',
              background: 'linear-gradient(135deg, rgba(26,108,246,0.06), rgba(109,40,217,0.04))',
              border: '1px solid rgba(26,108,246,0.12)',
              borderRadius: 10,
            }}
          >
            <Sparkles size={13} style={{ color: 'var(--nx-blue)', flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: 'var(--nx-text-secondary)' }}>
              AI-powered community management · Secured by Pentagon Analytics
            </span>
          </div>

          <p style={{ marginTop: 20, textAlign: 'center', fontSize: 12, color: 'var(--nx-text-muted)' }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: 'var(--nx-blue)', textDecoration: 'none', fontWeight: 500 }}>
              Sign in
            </Link>
          </p>
        </div>
      </div>

      <div style={{ position: 'relative', zIndex: 2, marginTop: 24, fontSize: 11, color: 'var(--nx-text-muted)' }}>
        © {new Date().getFullYear()} Pentagon Analytics. All rights reserved.
      </div>
    </div>
  );
};

export default Register;
